// ─── Auth service — real API ───────────────────────────────────────────────────
// Talks to the .NET backend at /api/v1/auth/* via apiClient. The mock helpers
// have been removed; sessionStorage tokens are populated by the real flow.

import type { AuthResponse, LoginCredentials, User } from '../types';
import apiClient from './client';
import logger from '../utils/logger';
import { ApiRequestError } from './ApiRequestError';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Array<{ code: string; message: string; field?: string }>;
}

interface BackendUser {
  publicId: string;
  companyPublicId: string;
  email: string;
  displayName: string;
  phone?: string | null;
  status: string;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
  twoFactorMethod: string;
  createdAtUtc: string;
  lastLoginUtc?: string | null;
  roles: string[];
}

interface BackendTokens {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  refreshToken: string;
  refreshTokenExpiresAtUtc: string;
}

interface BackendLoginResult {
  tokens: BackendTokens | null;
  user: BackendUser | null;
  twoFactorChallengeId: string | null;
  twoFactorMethod: string | null;
}

function unwrap<T>(envelope: ApiResponse<T>): T {
  if (!envelope.success || envelope.data === undefined) {
    const msg = envelope.message ?? 'Request failed';
    const err = { message: msg, code: envelope.errors?.[0]?.code ?? 'API_ERROR', statusCode: 0 };
    throw err;
  }
  return envelope.data;
}

// Membership roles are free text on the backend (a comma-separated nvarchar), and casing is
// not consistent across the data — staff rows are seeded lowercase ('admin') while personnel
// roles are PascalCase ('Driver', 'PA'). Every backend comparison uses OrdinalIgnoreCase, so
// match the same way here rather than depending on how a given row happens to be cased.
function hasRole(roles: string[] | undefined, target: string): boolean {
  return (roles ?? []).some((r) => r.trim().toLowerCase() === target);
}

function mapUser(b: BackendUser): User {
  // Backend stores DisplayName as a single field. Split on first space for the existing UI shape;
  // a future iteration can introduce real first/last name fields.
  const [firstName, ...rest] = (b.displayName ?? '').split(' ');
  const lastName = rest.join(' ').trim();

  // Map backend roles to the UI role enum. Web login is blocked for Driver/PA,
  // so only 'admin' and 'manager' are reachable here. Any other value is a bug.
  let role: User['role'];
  if (hasRole(b.roles, 'admin')) role = 'admin';
  else if (hasRole(b.roles, 'manager')) role = 'manager';
  else throw new ApiRequestError({ message: `Unrecognised web role: [${(b.roles ?? []).join(', ')}]`, code: 'AUTH_ROLE_UNKNOWN', statusCode: 403 });

  return {
    id: b.publicId,
    email: b.email,
    // Preserve the full display name (multi-token, possibly localised) so the UI
    // can render the complete value instead of just the first whitespace-split token.
    displayName: (b.displayName ?? '').trim() || b.email.split('@')[0],
    firstName: firstName ?? b.email.split('@')[0],
    lastName: lastName || '',
    role,
    createdAt: b.createdAtUtc,
    twoFactorEnabled: b.twoFactorEnabled,
    twoFactorMethod: (b.twoFactorMethod as User['twoFactorMethod']) ?? 'None'
  };
}

export interface TotpEnrollmentResult {
  secret: string;
  provisioningUri: string;
  backupCodes: string[];
}

export type TwoFactorMethod = 'Totp' | 'Email' | 'Sms';

// Discriminated result so the caller can branch on 2FA without a thrown error.
export type LoginOutcome =
  | { kind: 'authenticated'; user: User; token: string; refreshToken: string }
  | { kind: 'two-factor-required'; challengeId: string; method: TwoFactorMethod };

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginOutcome> {
    logger.info('Auth: login attempt', { email: credentials.email });

    const res = await apiClient.post<ApiResponse<BackendLoginResult>>('/auth/login', {
      email: credentials.email,
      password: credentials.password
    });
    const result = unwrap(res.data);

    if (result.twoFactorChallengeId) {
      // 2FA-enabled account: backend has held back tokens until the second factor is verified.
      // The caller (LoginPage) routes the user to /auth/verify-2fa carrying the challenge details.
      return {
        kind: 'two-factor-required',
        challengeId: result.twoFactorChallengeId,
        method: (result.twoFactorMethod ?? 'Email') as TwoFactorMethod
      };
    }

    if (!result.tokens || !result.user) {
      throw new ApiRequestError({ message: 'Login response was incomplete.', code: 'AUTH_INVALID', statusCode: 500 });
    }

    sessionStorage.setItem('auth_token', result.tokens.accessToken);
    sessionStorage.setItem('refresh_token', result.tokens.refreshToken);

    logger.info('Auth: login success', { userId: result.user.publicId });
    return {
      kind: 'authenticated',
      user: mapUser(result.user),
      token: result.tokens.accessToken,
      refreshToken: result.tokens.refreshToken
    };
  },

  async sendTwoFactorCode(challengeId: string): Promise<void> {
    const res = await apiClient.post<ApiResponse<unknown>>('/auth/send-2fa-code', { challengeId });
    if (!res.data?.success) {
      throw new ApiRequestError({ message: res.data?.message ?? 'Could not send code', code: 'API_ERROR', statusCode: 0 });
    }
  },

  // Verifies a 2FA code (TOTP, Email, SMS) or a backup code — backend accepts either via the
  // same endpoint. On success the response shape matches the normal login result, so the
  // caller stores tokens and routes to /dashboard.
  async verifyTwoFactor(challengeId: string, code: string): Promise<AuthResponse> {
    const res = await apiClient.post<ApiResponse<BackendLoginResult>>('/auth/verify-2fa', {
      challengeId, code
    });
    const result = unwrap(res.data);
    if (!result.tokens || !result.user) {
      throw new ApiRequestError({ message: 'Verification response was incomplete.', code: 'AUTH_INVALID', statusCode: 500 });
    }
    sessionStorage.setItem('auth_token', result.tokens.accessToken);
    sessionStorage.setItem('refresh_token', result.tokens.refreshToken);
    return {
      user: mapUser(result.user),
      token: result.tokens.accessToken,
      refreshToken: result.tokens.refreshToken
    };
  },

  async logout(): Promise<void> {
    const refresh = sessionStorage.getItem('refresh_token');
    try {
      if (refresh) {
        await apiClient.post('/auth/logout', { refreshToken: refresh });
      }
    } catch (e) {
      // best-effort — proceed to clear local storage even if the server roundtrip fails
      logger.warn('Auth: logout server call failed (clearing local session anyway)', e);
    }
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
    logger.info('Auth: logout');
  },

  async refreshToken(refreshToken: string): Promise<{ token: string }> {
    const res = await apiClient.post<ApiResponse<BackendTokens>>('/auth/refresh', { refreshToken });
    const tokens = unwrap(res.data);
    sessionStorage.setItem('auth_token', tokens.accessToken);
    sessionStorage.setItem('refresh_token', tokens.refreshToken);
    return { token: tokens.accessToken };
  },

  async getCurrentUser(): Promise<User> {
    const res = await apiClient.get<ApiResponse<BackendUser>>('/auth/me');
    return mapUser(unwrap(res.data));
  },

  // Public registration. Returns the new user envelope; the UI decides whether to redirect
  // to a "check your email" page. Default tenant assignment happens server-side.
  async register(input: { email: string; password: string; displayName: string; phone?: string | null }): Promise<User> {
    const res = await apiClient.post<ApiResponse<BackendUser>>('/auth/register', input);
    return mapUser(unwrap(res.data));
  },

  async confirmEmail(email: string, token: string): Promise<boolean> {
    const res = await apiClient.post<ApiResponse<boolean>>('/auth/confirm-email', { email, token });
    return unwrap(res.data);
  },

  // Same anti-enumeration contract as requestPasswordReset: 200 whether or not the address is
  // registered or already confirmed. A 429 (rate limit) still throws, with the server's message.
  async resendEmailConfirmation(email: string): Promise<void> {
    const res = await apiClient.post<ApiResponse<unknown>>('/auth/resend-email-confirmation', { email });
    if (!res.data?.success) {
      throw new ApiRequestError({ message: res.data?.message ?? 'Request failed', code: 'API_ERROR', statusCode: 0 });
    }
  },

  // Backend returns 200 even for unknown emails (anti-enumeration). The UI should always show
  // the same "check your email" message regardless of whether the address was registered.
  async requestPasswordReset(email: string): Promise<void> {
    const res = await apiClient.post<ApiResponse<unknown>>('/auth/request-password-reset', { email });
    if (!res.data?.success) {
      throw new ApiRequestError({ message: res.data?.message ?? 'Request failed', code: 'API_ERROR', statusCode: 0 });
    }
  },

  async completePasswordReset(email: string, token: string, newPassword: string): Promise<void> {
    const res = await apiClient.post<ApiResponse<unknown>>('/auth/complete-password-reset', {
      email,
      token,
      newPassword
    });
    if (!res.data?.success) {
      throw new ApiRequestError({ message: res.data?.message ?? 'Reset failed', code: 'API_ERROR', statusCode: 0 });
    }
  },

  // Enrolling TOTP immediately turns 2FA on (backend behavior). On next login the user will be
  // challenged for a 6-digit code from their authenticator app.
  async enrollTotp(): Promise<TotpEnrollmentResult> {
    const res = await apiClient.post<ApiResponse<{
      secret: string; provisioningUri: string; backupCodes: string[];
    }>>('/auth/2fa/enroll/totp');
    return unwrap(res.data);
  },

  // Enabling Email 2FA immediately turns 2FA on. Backend will dispatch a 6-digit code to the
  // user's email at login via the same SendGrid path we wired up.
  async enableEmail2FA(): Promise<string[]> {
    const res = await apiClient.post<ApiResponse<string[]>>('/auth/2fa/enable/email');
    return unwrap(res.data);
  },

  async disable2FA(): Promise<void> {
    const res = await apiClient.post<ApiResponse<unknown>>('/auth/2fa/disable');
    if (!res.data?.success) {
      throw new ApiRequestError({ message: res.data?.message ?? 'Disable failed', code: 'API_ERROR', statusCode: 0 });
    }
  }
};
